import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  ChipProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  PaperProps,
  Stack,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { getQuestionDifficultyChipColor } from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { IQuestion } from '../../../../store/QuestionSlice';
import {
  getQuestionListBySubTopic,
  loadMoreQuestionListBySubTopic,
  getSessionState,
} from '../../../../store/SessionSlice';
import { getSubTopicState } from '../../../../store/SubTopicSlice';

import './styles.css';
import AnswerListItem from '../components/AnswerListItem';
import QuestionCard from '../components/QuestionCard';
import BasicLoader from '../../../../components/Loaders/BasicLoader';

const extractContents = (html: string) => {
  const span = document.createElement('span');
  span.innerHTML = html;
  const result = span.textContent || span.innerText;
  return result.substring(0, 100);
};

const labelStyles: React.CSSProperties = {
  borderRadius: '4px',
  fontWeight: 600,
  marginRight: '5px',
};

const questionDetailContainerStyles: PaperProps['sx'] = {
  boxShadow: 1,
  padding: '15px',
};

export interface IQuestionSession extends IQuestion {
  order: number;
}

interface IAddQuestionsModal {
  open: boolean;
  onClose: () => void;
  onConfirm: (checkedQuestions: string[]) => void;
}

const AddQuestionsModal: React.FC<IAddQuestionsModal> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const dispatch = useDispatch();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const sessionState = useAppSelector(getSessionState);

  const { subTopic } = subTopicState;
  const { questionList, questionListStatus, session } = sessionState;
  const topicData = subTopic?.topic || undefined;

  // State
  const [questionListFilterData, setQuestionListFilterData] = useState({
    offset: 0,
    limit: 30,
  });
  const [selectedQuestion, setSelectedQuestion] = useState<
    IQuestionSession | undefined
  >(undefined);
  const [questions, setQuestions] = useState<IQuestionSession[]>([]);
  const [checkedQuestions, setCheckedQuestions] = useState<string[]>([]);

  useEffect(() => {
    if (subTopic) {
      dispatch(
        getQuestionListBySubTopic({
          ...questionListFilterData,
          subTopicIds: [subTopic._id],
        })
      );
    }
  }, []);

  useEffect(() => {
    if (questionList.length) {
      setQuestions(
        questionList.map((item, index) => ({
          ...item,
          order: index + 1,
        }))
      );
      setSelectedQuestion({ ...questionList[0], order: 1 });
    }
  }, [questionList]);

  const onQuestionChange = (question: IQuestionSession) => {
    setSelectedQuestion(question);
  };

  const handleCheckClick = (questionId: string, isChecked) => {
    let newCheckedQuestions = [...checkedQuestions];
    if (isChecked) {
      // newCheckedQuestions.push(questionId);
      newCheckedQuestions[0] = questionId;
    } else {
      newCheckedQuestions = newCheckedQuestions.filter(
        (item) => item !== questionId
      );
    }
    setCheckedQuestions(newCheckedQuestions);
  };

  const handleLoadMore = () => {
    if (subTopic) {
      dispatch(
        loadMoreQuestionListBySubTopic({
          ...questionListFilterData,
          offset: questionList.length,
          subTopicIds: [subTopic._id],
        })
      );
      setQuestionListFilterData((prevFilterData) => ({
        ...prevFilterData,
        offset: questionList.length,
      }));
    }
  };

  const isIncluded = (questionId: string) =>
    session.questions.some((item) => item.question._id === questionId);

  return (
    <Dialog
      PaperProps={{ sx: { minWidth: '75%' } }}
      open={open}
      onClose={onClose}
    >
      <DialogTitle>Add Questions</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Take your teaching to the next level with our latest feature! Enjoy
          the freedom to insert questions at any point between the resources,
          providing a tailored and interactive learning experience for your
          students.
        </DialogContentText>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          style={{ margin: '10px 0' }}
        >
          * Following Questions based on the {topicData?.curriculum}{' '}
          {topicData?.subject}, {topicData?.sortingOrder}.
          {subTopic?.sortingOrder} {subTopic?.name} *
        </Typography>
        <div style={{ padding: '5px 15px' }}>
          {questions.length && selectedQuestion ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <div className="question-list-item-container">
                  {questions.length
                    ? questions.map((question, index) => {
                        const _selected =
                          question._id === selectedQuestion?._id;
                        const _title = `Question ${(index + 1)
                          .toString()
                          .padStart(2, '0')}`;
                        let _description = '';
                        if (question.details) {
                          _description = extractContents(question.details);
                        }
                        const _chips: Array<ChipProps> = [
                          {
                            color: getQuestionDifficultyChipColor(
                              question.difficulty
                            ),
                            label: question.difficulty.toLowerCase(),
                          },
                        ];
                        if (isIncluded(question._id)) {
                          _chips.push({
                            color: 'default',
                            label: 'Included',
                          });
                        }
                        return (
                          <QuestionCard
                            key={index}
                            questionId={question._id}
                            isSelected={_selected}
                            isChecked={checkedQuestions.some(
                              (questionId) => questionId === question._id
                            )}
                            isIncluded={isIncluded(question._id)}
                            title={_title}
                            description={_description}
                            chips={_chips}
                            onCardClick={() => onQuestionChange(question)}
                            onCheckClick={handleCheckClick}
                          />
                        );
                      })
                    : null}

                  {sessionState.count > questions.length && (
                    <Stack direction="row" justifyContent="center">
                      {questionListStatus === 'loading' ? (
                        <BasicLoader />
                      ) : (
                        <Button onClick={handleLoadMore}>Load More</Button>
                      )}
                    </Stack>
                  )}
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={8} lg={8}>
                {selectedQuestion && (
                  <Paper sx={{ ...questionDetailContainerStyles }}>
                    {/* TOP LABELS AND BUTTONS */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        <FormControlLabel
                          label="Select"
                          control={
                            <Checkbox
                              disabled={isIncluded(selectedQuestion._id)}
                              checked={checkedQuestions.some(
                                (questionId) =>
                                  questionId === selectedQuestion._id
                              )}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                handleCheckClick(
                                  selectedQuestion._id,
                                  event.currentTarget.checked
                                )
                              }
                            />
                          }
                        />
                        <Chip
                          label={`Question ${selectedQuestion?.order
                            .toString()
                            .padStart(2, '0')}`}
                          style={labelStyles}
                        />
                        <Chip
                          label={selectedQuestion?.difficulty}
                          style={labelStyles}
                          color={getQuestionDifficultyChipColor(
                            selectedQuestion?.difficulty || 'EASY'
                          )}
                        />
                        {isIncluded(selectedQuestion._id) && (
                          <Chip
                            label="Included"
                            style={labelStyles}
                            color="default"
                          />
                        )}
                      </Box>
                    </Stack>
                    {/* QUESTION DETAILS */}
                    <div style={{ padding: '10px 0' }}>
                      <div
                        className="view ql-editor ql-viewer"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: selectedQuestion?.details || '',
                        }}
                        style={{ marginBottom: '20px' }}
                      />
                      {/* ANSWER LIST */}
                      <div>
                        <Grid container spacing={2}>
                          {selectedQuestion?.answers.map((answer, index) => (
                            <Grid key={index} item xs={12}>
                              <AnswerListItem answer={answer} />
                            </Grid>
                          ))}
                        </Grid>
                      </div>
                    </div>
                  </Paper>
                )}
              </Grid>
            </Grid>
          ) : (
            <div style={{ padding: '30px 0' }}>
              {questionListStatus === 'loading' ? (
                <BasicLoader />
              ) : (
                <Typography
                  color="secondary"
                  variant="h6"
                  textAlign="center"
                  sx={{ m: '30px 0' }}
                >
                  No questions are available for this subtopic.
                </Typography>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions style={{ padding: '20px' }}>
        <Button color="inherit" variant="contained" onClick={onClose}>
          Dismiss
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!checkedQuestions.length}
          onClick={() => onConfirm(checkedQuestions)}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddQuestionsModal;
